import * as React from "react";
import {FlowNode as NodeType} from "./types";

type Props = {
    node: NodeType;
    highlightState?: "primary" | "connected";
    isDimmed?: boolean;
    isStanding?: boolean;
    renderShape?: boolean;
    renderLabel?: boolean;
};

const HIGHLIGHT_STROKE_PRIMARY = "#c53030";
const HIGHLIGHT_STROKE_CONNECTED = "#dd6b20";
const HIGHLIGHT_WIDTH_PRIMARY = 3;
const HIGHLIGHT_WIDTH_CONNECTED = 2.5;
const BASE_STROKE_WIDTH = 1.5;
const STANDING_MARKER_OUTER_RADIUS = 6.2;
const STANDING_MARKER_INNER_RADIUS = 2.6;
const STANDING_MARKER_OFFSET_X = 12;
const STANDING_MARKER_OFFSET_Y = 12;

const FlowNode = ({
    node,
    highlightState,
    isDimmed,
    isStanding,
    renderShape = true,
    renderLabel = true
}: Props) => {
    const isStart = node.kind === "start";
    const baseFill = isStart ? "#2b6cb0" : "#ebf8ff";
    const baseStroke = isStart ? "#1a365d" : "#2b6cb0";
    const baseTextColor = isStart ? "#fff" : "#1a365d";
    const fill = isDimmed ? "#edf2f7" : baseFill;
    const stroke = isDimmed ? "#cbd5e0" : baseStroke;
    const textColor = isDimmed ? "#a0aec0" : baseTextColor;
    const fontSize = isStart ? 12 : 13;
    const fontWeight = isStart ? 700 : 600;
    const highlightStroke = highlightState === "primary"
        ? HIGHLIGHT_STROKE_PRIMARY
        : highlightState === "connected"
            ? HIGHLIGHT_STROKE_CONNECTED
            : stroke;
    const highlightWidth = highlightState === "primary"
        ? HIGHLIGHT_WIDTH_PRIMARY
        : highlightState === "connected"
            ? HIGHLIGHT_WIDTH_CONNECTED
            : BASE_STROKE_WIDTH;
    const standingStroke = "#38a169";
    const standingFill = "#38a169";
    const standingOuterFill = "#f7fafc";
    const standingCx = Math.max(10, node.width - STANDING_MARKER_OFFSET_X);
    const standingCy = STANDING_MARKER_OFFSET_Y;

    return (
        <g transform={`translate(${node.x}, ${node.y})`}>
            {renderShape ? (
                <rect
                    width={node.width}
                    height={node.height}
                    rx={isStart ? 10 : 16}
                    ry={isStart ? 10 : 16}
                    fill={fill}
                    stroke={highlightStroke}
                    strokeWidth={highlightWidth}
                />
            ) : null}
            {renderLabel && isStanding && (
                <g>
                    <circle
                        cx={standingCx}
                        cy={standingCy}
                        r={STANDING_MARKER_OUTER_RADIUS}
                        fill={standingOuterFill}
                        stroke={standingStroke}
                        strokeWidth={2}
                    />
                    <circle
                        cx={standingCx}
                        cy={standingCy}
                        r={STANDING_MARKER_INNER_RADIUS}
                        fill={standingFill}
                    />
                </g>
            )}
            {renderLabel ? (
                <text
                    x={node.width / 2}
                    y={node.height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                    fill={textColor}
                    style={{userSelect: "none"}}
                >
                    {node.label}
                </text>
            ) : null}
        </g>
    );
};

export default FlowNode;
